import { Component, EventEmitter, Input, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup, Validators
} from '@angular/forms';
import { Observable, Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { TextInputModel } from '../../../shared/components/controls/text-input-control/TextInputModel';
import {
  Constants,
  WireAbaCodes,
  intermediaryErrorMap,
  isoInputFieldErrorMap, 
  isoInputZipErrorMap, 
  thirdPartyCheckFieldsErrorMap
} from '../../../shared/utilities/Constants';
import {
  firstCharacterAlphaNumeric,
  phoneNumberWithPossibleExtension
} from '../../../shared/utilities/FormValidators';
import { intermediaryAllowedChars, isoFieldsAllowedChars, zipCodeAllowedChars, zipCodePattern } from '../../../shared/utilities/regex-patterns';
import { AlternatingTextLinkControl, ShowHideTextControl } from '../../models/form/FormControlAlternatingTextLink';
import { WireInfo } from '../../models/wire/WireInfo';
import { createControl } from '../../utilities/formUtilities';
import { Store } from '@ngrx/store';
import { selectTemplatePrefilledFlowStarted, selectTemplatesList } from '../../../account-info-header/reducers';
import { selectIsFedWire, selectTemplateId, selectUseStandingAuth, selectUseTemplate, selectWireAbaCode, selectWireDetailsError, selectWireDetailsWarning } from '../../reducers';
import { selectWireDetailsWarningStandingAuth, selectWireDetailsErrorStandingAuth } from '../../../standing-authorizations/reducers';
import { Actions, ofType } from '@ngrx/effects';
import { TransactionFormActions } from '../../actions';
import { SelectControl } from '../../models/OptionDropdownSelect';
import { Country } from '../../models/shared/Country';
import { FormatIsoAddressFieldsService } from '../../../shared/services/format-isoAddressFields.service';
import { CountryDescriptionFromCode } from '../../../shared/utilities/CountryDescriptionFromCode';
@Component({
  standalone: false,
  selector: 'mm-wire-intermediary-iso',
  templateUrl: './wire-intermediary-iso.component.html',
  styleUrls: ['./wire-intermediary-iso.component.scss']
})
export class WireIntermediaryIsoComponent implements OnInit {
  @Input() parentForm: UntypedFormGroup;
  @Input() useSloa: boolean;
  @Input() useTemplate: boolean;
  @Input() wireInfo$: Observable<WireInfo>;
  @Input() missingIntermediaryAddress: boolean;
  @Input() wireIntermediary$: Observable<string>;
  @Input() isFromCreateTransaction: boolean;
  @Input() canDisplayCreditToInfo: boolean;
  @Input() palAccountInContext: boolean = false;
  @Input() countryList$: Observable<Country[]>;
  @Input() fromTemplateStartTransaction: boolean;
  wireInputFields = new EventEmitter<string>();
  isFedWire$: Observable<boolean>;
  useSloa$: Observable<boolean>;
  useTemplate$: Observable<boolean>;
  wireAbaCode$: Observable<string>;
  wireDetailsError$: Observable<string>;
  wireDetailsWarning$: Observable<string>;
  showIntermediaryFields$: Observable<boolean> | undefined;
  destroy$ = new Subject<void>();
  hideAddressFields: string | null;
  isISOCompliant: boolean;
  intermediaryAllowedCharsRe = new RegExp(intermediaryAllowedChars);
  addRemoveInteremediaryToolTipMessage: string = Constants.intermediaryISOTooltip;
  warningIntermediaryDescription: string = Constants.intermediaryAbaAccountNumberWarningDescription;
  intermediaryNameAddressSubHeader: string = Constants.intermediaryNameAddressUseSloaSubHeader;
  intermediaryUseSloaAddress: string;
  intermediaryLabel: string = Constants.intermediaryHeader;
  intermediaryDetalis: string = Constants.intermediaryDetailsHeader;
  useSloaIntermediaryAccountNumber: string;
  nonIsoCompliantErrorHeader: string = Constants.isoCompliantWireTemplateErrorBannerHeader;
  nonIsoCompliantErrorDescription: string = Constants.isoWireTemplateErrorBannerDescriptionIntermediary;
  nonIsoCompliantErrorAddressDetails: string;
  isFromTemplateAndNonIsoCompliant: boolean = false;
  isFourStep: boolean = false;
  intermediaryUseSloaAccountNumberHeader: string = "Account number";
  intermediaryAccountNumber: string;
  intermediaryBIC: string;
  intermediaryClearingSysId: string;
  recipientBankTooltip: string = Constants.recipientBankTooltip;
  @Input() isFromEditReview: boolean;

  readonly controlNames = {
    intermediarySelection: 'intermediarySelection',
    name: 'intermediaryName',
    nameCont: 'intermediaryNameCont',
    address: 'intermediaryAddress',
    addressCont: 'intermediaryAddressCont',
    accountNumber: 'intermediaryAccountNumber',
    streetNumber: 'intermediaryStreetNumber',
    streetName: 'intermediaryStreetName',
    apartmentSuiteEtc: 'intermediaryApartmentSuiteEtc',
    townLocationName: 'intermediaryTownLocationName',
    city: 'intermediaryCity',
    district: 'intermediaryDistrict',
    state: 'intermediaryState',
    zipCode: 'intermediaryZip',
    phone: 'intermediaryPhone',
    country: 'intermediaryCountry',
    department: 'intermediaryDepartment',
    buildingName: 'intermediaryBuildingName',
    floor: 'intermediaryFloor'
  };
  countryListDropdown: SelectControl = {
    title: 'Country',
    controlName: this.controlNames.country,
    options: [],
    validationMessage: 'Country selection is required.',
  };

  intermediaryAddRemoveLinkModel: AlternatingTextLinkControl = {
    controlName: this.controlNames.intermediarySelection,
    text1: {
      key: 'Add an intermediary bank',
      value: 'no'
    },
    text2: {
      key: 'Remove intermediary bank',
      value: 'yes'
    },
    tooltip: {
      position: 'right',
      containsLink: false,
      content: Constants.intermediaryISOTooltip
    }
  }

  intermediaryShowHideLinkModel: ShowHideTextControl = {
    controlName: 'addressSelection',
    text1: {
      key: 'Show additional address fields',
      value: 'no'
    },
    text2: {
      key: 'Hide additional address fields',
      value: 'yes'
    }
  }
  
  nameModel: TextInputModel = {
    controlName: this.controlNames.name,
    label:'Intermediary account name',
    hasGreyText: false,
    htmlMaxLength: 140,
  };

  accountNumberModel: TextInputModel = {
    controlName: this.controlNames.accountNumber,
    label: 'Intermediary account number',
    hasGreyText: false,
    htmlMaxLength: 34
  };

  streetNumberModel: TextInputModel = {
    controlName: this.controlNames.streetNumber,
    label: 'Street number',
    hasGreyText: true,
    greyText: '(recommended)',
    htmlMaxLength: 16
  };

  streetNameModel: TextInputModel = {
    controlName: this.controlNames.streetName,
    label: 'Street name',
    hasGreyText: true,
    greyText: '(recommended)',
    htmlMaxLength: 70
  };

  apartmentSuiteModel: TextInputModel = {
    controlName: this.controlNames.apartmentSuiteEtc,
    label: 'Apartment, suite, etc.',
    hasGreyText: true,
    greyText: '(optional)',
    htmlMaxLength: 70
  };

  townLocationNameModel: TextInputModel = {
    controlName: this.controlNames.townLocationName,
    label: 'Sub-locality',
    hasGreyText: true,
    greyText: '(optional)',
    htmlMaxLength: 35
  };

  cityModel: TextInputModel = {
    controlName: this.controlNames.city,
    label: 'City',
    hasGreyText: false,
    htmlMaxLength: 35
  };

  districtModel: TextInputModel = {
    controlName: this.controlNames.district,
    label: 'District',
    hasGreyText: true,
    greyText: '(optional)',
    htmlMaxLength: 35
  };

  stateModel: TextInputModel = {
    controlName: this.controlNames.state,
    label: 'State',
    hasGreyText: false,
    htmlMaxLength: 35
  };

  zipModel: TextInputModel = {
    controlName: this.controlNames.zipCode,
    label: 'ZIP code',
    hasGreyText: true,
    greyText: '(recommended)',
    htmlMaxLength: 16
  };
  phoneModel: TextInputModel = {
    controlName: this.controlNames.phone,
    label: 'Intermediary phone',
    hasGreyText: true,
    greyText: Constants.optionalDisplayText,
  };
  departmentModel: TextInputModel = {
    controlName: this.controlNames.department,
    label: 'Department',
    hasGreyText: true,
    greyText: '(optional)',
    htmlMaxLength: 70,
    hasIconTooltip: true,
    tooltip: {
      position: 'right',
      containsLink: false,
      content: Constants.departmentTooltip
    }
  };

  buildingNameModel: TextInputModel = {
    controlName: this.controlNames.buildingName,
    label: 'Building name',
    hasGreyText: true,
    greyText: '(optional)',
    htmlMaxLength: 35
  };

  floorModel: TextInputModel = {
    controlName: this.controlNames.floor,
    label: 'Floor',
    hasGreyText: true,
    greyText: '(optional)',
    htmlMaxLength: 70
  };

  // International Models

  intlApartmentSuiteModel: TextInputModel = {
    controlName: this.controlNames.apartmentSuiteEtc,
    label: 'Flat, suite, etc.',
    hasGreyText: true,
    greyText: '(optional)',
    htmlMaxLength: 70
  };

  intlCityModel: TextInputModel = {
    controlName: this.controlNames.city,
    label: 'City/Town/Locality',
    hasGreyText: false,
    htmlMaxLength: 35
  };

  intlStateModel: TextInputModel = {
    controlName: this.controlNames.state,
    label: 'State/Province/Region',
    hasGreyText: false,
    htmlMaxLength: 35
  };

  intlZipModel: TextInputModel = {
    controlName: this.controlNames.zipCode,
    label: 'Postal code',
    hasGreyText: true,
    greyText: '(recommended)',
    htmlMaxLength: 16
  };

  nameContModel: TextInputModel = {
    controlName: this.controlNames.nameCont,
    label: 'Intermediary account name continued',
    hasGreyText: true,
    greyText: Constants.optionalDisplayText,
    htmlMaxLength: 40,
  };

  addressModel: TextInputModel = {
    controlName: this.controlNames.address,
    label: 'Intermediary address',
    hasGreyText: false,
    htmlMaxLength: 40,
    secondLineGreyText: ''
  };

  addressContModel: TextInputModel = {
    controlName: this.controlNames.addressCont,
    label: 'Intermediary address continued',
    hasGreyText: true,
    greyText: Constants.optionalDisplayText,
    htmlMaxLength: 40,
  };

  constructor(private store: Store, private continueClickInvalid$: Actions, private formatIsoAddressFieldsService: FormatIsoAddressFieldsService) { }

  ngOnInit(): void {
    this.isISOCompliant = this.parentForm?.value?.isISOCompliant;
    this.showIntermediaryFields$ = this.wireIntermediary$.pipe(map((selection) => selection === 'yes'));
    this.isFedWire$ = this.store.select(selectIsFedWire);
    this.useSloa$ = this.store.select(selectUseStandingAuth);
    this.useTemplate$ = this.store.select(selectUseTemplate);
    this.wireAbaCode$ = this.store.select(selectWireAbaCode);
    this.intermediaryAccountNumber = (this.parentForm?.value?.intermediaryAccountNumber != null && this.parentForm?.value?.intermediaryAccountNumber != undefined) ? this.parentForm?.value?.intermediaryAccountNumber : 
                                    ((this.parentForm?.value?.intermediaryIBAN != null && this.parentForm?.value?.intermediaryIBAN != undefined) ? this.parentForm?.value?.intermediaryIBAN : null);
    this.intermediaryBIC = this.parentForm?.value?.intermediaryBIC;
    this.intermediaryClearingSysId = this.parentForm?.value?.intermediaryClearingSysId;
    this.intermediaryUseSloaAccountNumberHeader = (this.parentForm?.value?.intermediaryIBAN != null && this.parentForm?.value?.intermediaryIBAN != undefined) ? 'International account number (IBAN)' : this.intermediaryUseSloaAccountNumberHeader; 

      this.countryList$?.pipe(takeUntil(this.destroy$)).subscribe((countries) => {
        this.countryListDropdown.options = countries.map((country) => {
          return { value: country.code, name: country.description };
        });
      });

    if (this.isFromCreateTransaction) {
      this.wireDetailsError$ = this.store.select(selectWireDetailsError);
      this.wireDetailsWarning$ = this.store.select(selectWireDetailsWarning);
    }
    else {
      this.wireDetailsError$ = this.store.select(selectWireDetailsErrorStandingAuth);
      this.wireDetailsWarning$ = this.store.select(selectWireDetailsWarningStandingAuth);
    }

    if ((this.department?.value && this.department?.value !== "") || (this.buildingName?.value && this.buildingName?.value !== "") || (this.floor?.value && this.floor?.value !== "")) {
      this.hideAddressFields = "no";
    } else {
      this.hideAddressFields = "yes";
    }

    combineLatest([this.wireInfo$, this.showIntermediaryFields$])
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(this.store.select(selectTemplatePrefilledFlowStarted)),
        filter(([[wireInfo, showIntermediary], tempPrefilledFlow]) => !tempPrefilledFlow)
      )
      .subscribe(([[wireInfo, showIntermediary], tempPrefilledFlow]) => {
        let name = '';
        let nameCont = '';
        let address = '';
        let addressCont = '';
        let accountNumber = '';
        let streetNumber = '';
        let streetName = '';
        let apartmentSuiteEtc = '';
        let townLocationName = '';
        let city = '';
        let district = '';
        let state = '';
        let zipCode = '';
        let department = '';
        let buildingName = '';
        let floor = '';
        let phone = '';
        let country = 'US';
        let shouldDisable = false;
        let shouldEnableForMissingAddress = false;
        let setValues = false;

        if (this.useSloa) {
          shouldDisable = true;
          // this is for converted accounts - TDA didn't require an intermediary address and letting them enter it will make the standing auth valid
          if (this.missingIntermediaryAddress) {
            shouldEnableForMissingAddress = true;
          }
          name = wireInfo.receivingBankDetailsIso.intermediaryName;
          accountNumber = wireInfo.receivingBankDetailsIso.intermediaryAccount;
          streetNumber = wireInfo.receivingBankDetailsIso.intermediaryAddress.buildingNumber;
          streetName = wireInfo.receivingBankDetailsIso.intermediaryAddress.streetName;
          apartmentSuiteEtc = wireInfo.receivingBankDetailsIso.intermediaryAddress.room;
          city = wireInfo.receivingBankDetailsIso.intermediaryAddress.townName;
          state = wireInfo.receivingBankDetailsIso.intermediaryAddress.countrySubDivision;
          zipCode = wireInfo.receivingBankDetailsIso.intermediaryAddress.postCode;
          department = wireInfo.receivingBankDetailsIso.intermediaryAddress.department;
          buildingName = wireInfo.receivingBankDetailsIso.intermediaryAddress.buildingName;
          floor = wireInfo.receivingBankDetailsIso.intermediaryAddress.floor;
          phone =  wireInfo.receivingBankDetailsIso.intermediaryPhone;
          country = wireInfo.receivingBankDetailsIso.intermediaryAddress.country;
          townLocationName =  wireInfo.receivingBankDetailsIso.intermediaryAddress.townLocationName;
          district = wireInfo.receivingBankDetailsIso.intermediaryAddress.districtName;
          setValues = true;         
          this.useSloaIntermediaryAccountNumber = wireInfo.receivingBankDetailsIso.intermediaryAccount;
        } else {
          this.nameModel.label = 'Account name';
          this.accountNumberModel.label = 'Account number';
        }

        if (
          wireInfo.abaStatusCode ===
          WireAbaCodes.ABA_WITH_WARNING_TWO_INSTITUTIONS &&
          !this.useSloa
        ) {
          this.intermediarySelection?.setValue('yes');
          this.intermediarySelection?.disable();
          name = wireInfo.receivingBankDetailsIso.intermediaryName;
          accountNumber = wireInfo.receivingBankDetailsIso.intermediaryAccount;
          streetNumber = wireInfo.receivingBankDetailsIso.intermediaryAddress.buildingNumber;
          streetName = wireInfo.receivingBankDetailsIso.intermediaryAddress.streetName;
          apartmentSuiteEtc = wireInfo.receivingBankDetailsIso.intermediaryAddress.room;
          city = wireInfo.receivingBankDetailsIso.intermediaryAddress.townName;
          state = wireInfo.receivingBankDetailsIso.intermediaryAddress.countrySubDivision;
          zipCode = wireInfo.receivingBankDetailsIso.intermediaryAddress.postCode;
          department = wireInfo.receivingBankDetailsIso.intermediaryAddress.department;
          buildingName = wireInfo.receivingBankDetailsIso.intermediaryAddress.buildingName;
          floor = wireInfo.receivingBankDetailsIso.intermediaryAddress.floor;
          phone =  wireInfo.receivingBankDetailsIso.intermediaryPhone; // TODO: SAC-339442 format phone from mocked back end | this.formatIntermediaryPhoneNumber(wireInfo);
          shouldDisable = true;
          setValues = true;
        } else if (
          this.needToResetPrepopulatedIntermediaryFields(wireInfo) &&
          !this.useSloa
        ) {
          this.intermediarySelection?.setValue('no');
          this.intermediarySelection?.enable();
          this.name?.reset();
          this.name?.enable();
          this.accountNumber?.reset();
          this.accountNumber?.enable();
          this.streetName?.reset();
          this.streetName?.enable();
          this.streetNumber?.reset();
          this.streetNumber?.enable();
          this.apartmentSuiteEtc?.reset();
          this.apartmentSuiteEtc?.enable();
          this.townLocationName?.reset();
          this.townLocationName?.enable();
          this.city?.reset();
          this.city?.enable();
          this.district?.reset();
          this.district?.enable();
          this.state?.reset();
          this.state?.enable();
          this.zipCode?.reset();
          this.zipCode?.enable();
          this.department?.reset();
          this.department?.enable();
          this.buildingName?.reset();
          this.buildingName?.enable();
          this.floor?.reset();
          this.floor?.enable();
          this.phone?.reset();
          this.phone?.enable();
          shouldDisable = false;
          setValues = false;
        }

        if (showIntermediary) {
          if(this.useSloa) {
            const intermediaryCountry = wireInfo?.receivingBankDetailsIso?.intermediaryAddress?.country?.length == 2 ? CountryDescriptionFromCode(wireInfo?.receivingBankDetailsIso?.intermediaryAddress?.country, this.countryListDropdown.options): wireInfo?.receivingBankDetailsIso?.intermediaryAddress?.country;
            this.isFourStep = this.parentForm?.value?.isFourStep;
            this.intermediaryNameAddressSubHeader = Constants.intermediaryNameAddressUseSloaSubHeader;
            this.intermediaryLabel = this.parentForm.controls.aba?.value ? Constants.intermediaryHeader : '';
            this.intermediaryUseSloaAddress = this.getIntermediarySloaReadOnlyAddress(wireInfo.receivingBankDetailsIso.intermediaryName, wireInfo.receivingBankDetailsIso.intermediaryAddress.buildingNumber, wireInfo.receivingBankDetailsIso.intermediaryAddress.streetName, wireInfo.receivingBankDetailsIso.intermediaryAddress.room, wireInfo.receivingBankDetailsIso.intermediaryAddress.department, wireInfo.receivingBankDetailsIso.intermediaryAddress.buildingName, wireInfo.receivingBankDetailsIso.intermediaryAddress.floor, wireInfo.receivingBankDetailsIso.intermediaryAddress.townLocationName, wireInfo.receivingBankDetailsIso.intermediaryAddress.townName, wireInfo.receivingBankDetailsIso.intermediaryAddress.districtName, wireInfo.receivingBankDetailsIso.intermediaryAddress.countrySubDivision, wireInfo.receivingBankDetailsIso.intermediaryAddress.postCode, intermediaryCountry);
          }
          createControl(
            this.parentForm,
            this.controlNames.name,
            name,
            [
              Validators.required,
              Validators.pattern(isoFieldsAllowedChars)
            ],
            undefined,
            shouldDisable,
            setValues
          );
          createControl(
            this.parentForm,
            this.controlNames.nameCont,
            nameCont,
            [
              Validators.pattern(isoFieldsAllowedChars)
            ],
            undefined,
            setValues
          );
          createControl(
            this.parentForm,
            this.controlNames.address,
            address,
            [
              Validators.pattern(isoFieldsAllowedChars)
            ],
            undefined,
            setValues
          );
          createControl(
            this.parentForm,
            this.controlNames.addressCont,
            addressCont,
            [
              Validators.pattern(isoFieldsAllowedChars)
            ],
            undefined,
            setValues
          );
          createControl(
            this.parentForm,
            this.controlNames.accountNumber,
            accountNumber,
            [
              Validators.required,
              Validators.pattern(isoFieldsAllowedChars),
            ],
            undefined,
            shouldDisable,
            setValues
          );
          createControl(
            this.parentForm,
            this.controlNames.streetNumber,
            streetNumber,
            [
              Validators.pattern(isoFieldsAllowedChars),
            ],
            undefined,
            shouldEnableForMissingAddress ? false : shouldDisable,
            setValues
          );
          createControl(
            this.parentForm,
            this.controlNames.streetName,
            streetName,
            [
              Validators.pattern(isoFieldsAllowedChars),
            ],
            undefined,
            shouldEnableForMissingAddress ? false : shouldDisable,
            setValues
          );
          createControl(
            this.parentForm,
            this.controlNames.apartmentSuiteEtc,
            apartmentSuiteEtc,
            [
              Validators.pattern(isoFieldsAllowedChars),
            ],
            undefined,
            shouldEnableForMissingAddress ? false : shouldDisable,
            setValues
          );
          createControl(
            this.parentForm,
            this.controlNames.townLocationName,
            townLocationName,
            [
              Validators.pattern(isoFieldsAllowedChars)
            ],
            undefined,
            this.useSloa
          );
          createControl(
            this.parentForm,
            this.controlNames.city,
            city,
            [
              Validators.required,
              Validators.pattern(isoFieldsAllowedChars),
            ],
            undefined,
            shouldEnableForMissingAddress ? false : shouldDisable,
            setValues
          );
          createControl(
            this.parentForm,
            this.controlNames.district,
            district,
            [
              Validators.pattern(isoFieldsAllowedChars)
            ],
            undefined,
            this.useSloa
          );
          createControl(
            this.parentForm,
            this.controlNames.state,
            state,
            [
              Validators.required,
              Validators.pattern(isoFieldsAllowedChars),
            ],
            undefined,
            shouldEnableForMissingAddress ? false : shouldDisable,
            setValues
          );
          createControl(
            this.parentForm,
            this.controlNames.zipCode,
            zipCode,
            [
              Validators.pattern(zipCodePattern)
            ],
            undefined,
            shouldEnableForMissingAddress ? false : shouldDisable,
            setValues
          );
          createControl(
            this.parentForm,
            this.controlNames.country,
            country,
            undefined,
            undefined,
            true,
            true
          );
          createControl(
            this.parentForm,
            this.controlNames.phone,
            phone,
            [phoneNumberWithPossibleExtension, Validators.maxLength(22)],
            undefined,
            shouldDisable,
            setValues
          );
          createControl(
            this.parentForm,
            this.controlNames.department,
            department,
            [
              Validators.pattern(isoFieldsAllowedChars),
            ],
            undefined,
            shouldEnableForMissingAddress ? false : shouldDisable,
            setValues
          );
          createControl(
            this.parentForm,
            this.controlNames.buildingName,
            buildingName,
            [
              Validators.pattern(isoFieldsAllowedChars),
            ],
            undefined,
            shouldEnableForMissingAddress ? false : shouldDisable,
            setValues
          );
          createControl(
            this.parentForm,
            this.controlNames.floor,
            floor,
            [
              Validators.pattern(isoFieldsAllowedChars),
            ],
            undefined,
            shouldEnableForMissingAddress ? false : shouldDisable,
            setValues
          );
          
        } else {
          this.parentForm.removeControl(this.controlNames.name);
          this.parentForm.removeControl(this.controlNames.accountNumber);
          this.parentForm.removeControl(this.controlNames.streetNumber);
          this.parentForm.removeControl(this.controlNames.streetName);
          this.parentForm.removeControl(this.controlNames.apartmentSuiteEtc);
          this.parentForm.removeControl(this.controlNames.townLocationName);
          this.parentForm.removeControl(this.controlNames.city);
          this.parentForm.removeControl(this.controlNames.district);
          this.parentForm.removeControl(this.controlNames.state);
          this.parentForm.removeControl(this.controlNames.zipCode);
          this.parentForm.removeControl(this.controlNames.department);
          this.parentForm.removeControl(this.controlNames.buildingName);
          this.parentForm.removeControl(this.controlNames.floor);
          this.parentForm.removeControl(this.controlNames.phone);
          this.parentForm.removeControl(this.controlNames.country);
        }       
      });

    //TODO: remove after a more generic solution for scrolling to validation messages is implemented
    //added temporarily for green SLOAs with missing address info
    this.continueClickInvalid$.pipe(
      ofType(TransactionFormActions.continueClickInvalid),
      takeUntil(this.destroy$)
    )
      .subscribe(() => {
        // if (this.missingIntermediaryAddress && this.addressError) {
        //   const elem = document.getElementById(this.controlNames.address);
        if (this.missingIntermediaryAddress && this.streetNameError) {
          const elem = document.getElementById(this.controlNames.streetName);
          setTimeout(() => {
            elem?.focus();
            elem?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'start' });
          });
        }
      });

    if (this.fromTemplateStartTransaction || this.isFromEditReview) {
      combineLatest([
        this.store.select(selectTemplatesList),
        this.store.select(selectTemplateId)
      ]).pipe(takeUntil(this.destroy$)).subscribe(([templates, templateId]) => {
        const templateInContext = templates.find(template => template.draftId === templateId);
        if (templateInContext && templateInContext.draftObject.intermediary?.id && !templateInContext.draftObject.isIsoCompliant) {
          this.isFromTemplateAndNonIsoCompliant = true;
          this.name?.setValue(templateInContext.draftObject.intermediary?.mailingDetails?.namePartOne + ' '
          + templateInContext.draftObject.intermediary?.mailingDetails?.namePartTwo);
          this.nonIsoCompliantErrorAddressDetails = templateInContext.draftObject.intermediary?.mailingDetails?.addressLineOne + ' ' 
            + templateInContext.draftObject.intermediary?.mailingDetails?.addressLineTwo;
        }
      })
    }
  }

  getIntermediarySloaReadOnlyAddress(name?: string, streetNumber?: string, streetName?: string, apartmentSuiteEtc?: string, department?: string, buildingName?: string, floor?: string, townLocationName?: string, city?: string, district?: string, state?: string, zipCode?: string, country?: string) {
    return this.formatIsoAddressFieldsService.getFieldsValue(name) +
      this.formatIsoAddressFieldsService.getNextLine(name) +
      this.formatIsoAddressFieldsService.getFieldsValue(streetNumber ? streetNumber + " " + streetName : streetName) +
      this.formatIsoAddressFieldsService.getNextLine(streetNumber ? streetNumber + " " + streetName : streetName) +
      this.formatIsoAddressFieldsService.getFieldsValue(apartmentSuiteEtc, department, buildingName, floor) +
      this.formatIsoAddressFieldsService.getFieldsValue(department, buildingName, floor) +
      this.formatIsoAddressFieldsService.getFieldsValue(buildingName, floor) +
      this.formatIsoAddressFieldsService.getFieldsValue(floor) +
      this.formatIsoAddressFieldsService.getNextLine(apartmentSuiteEtc, department, buildingName, floor) +
      this.formatIsoAddressFieldsService.getFieldsValue(townLocationName, city, district, state, zipCode) +
      this.formatIsoAddressFieldsService.getFieldsValue(city, district, state, zipCode) +
      this.formatIsoAddressFieldsService.getFieldsValue(district, state, zipCode) +
      this.formatIsoAddressFieldsService.getFieldsValue(state, zipCode) +
      this.formatIsoAddressFieldsService.getFieldsValue(zipCode) +
      this.formatIsoAddressFieldsService.getNextLine(townLocationName, city, district, state, zipCode) +
      this.formatIsoAddressFieldsService.getFieldsValue(country) +
      this.formatIsoAddressFieldsService.getNextLine(country) 
  }

  isClosedAddressFields($event: boolean) {
    if ($event == false) {
      this.hideAddressFields = "yes";
      this.department?.reset();
      this.buildingName?.reset();
      this.floor?.reset();
    } else {
      this.hideAddressFields = "no";
    }
  }

  ngOnDestroy(): void {
    Object.keys(this.controlNames).forEach((name) => {
      const names = this.controlNames;
      this.parentForm?.removeControl(
        this.controlNames[name as keyof typeof names]
      );
    });

    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  onBlurFieldsTrimWhiteSpace(controlName: string) {
    const selectedControl = this.parentForm?.get(this.controlNames[controlName as keyof typeof this.controlNames]);
    selectedControl?.setValue(selectedControl?.value?.trim());
  }

  get intermediarySelection(): UntypedFormControl | undefined {
    return this.parentForm?.get(
      this.controlNames.intermediarySelection
    ) as UntypedFormControl;
  }

  get name(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.name) as UntypedFormControl;
  }

  get nameCont(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.nameCont) as UntypedFormControl;
  }

  get address(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.address) as UntypedFormControl;
  }

  get addressCont(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.addressCont) as UntypedFormControl;
  }

  get accountNumber(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.accountNumber) as UntypedFormControl;
  }

  get streetNumber(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.streetNumber) as UntypedFormControl;
  }

  get streetName(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.streetName) as UntypedFormControl;
  }

  get apartmentSuiteEtc(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.apartmentSuiteEtc) as UntypedFormControl;
  }

  get townLocationName(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.townLocationName) as UntypedFormControl;
  }

  get city(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.city) as UntypedFormControl;
  }

  get district(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.district) as UntypedFormControl;
  }

  get state(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.state) as UntypedFormControl;
  }

  get zipCode(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.zipCode) as UntypedFormControl;
  }
  
  get department(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.department) as UntypedFormControl;
  }

  get buildingName(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.buildingName) as UntypedFormControl;
  }

  get floor(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.floor) as UntypedFormControl;
  }

  get phone(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.phone) as UntypedFormControl;
  }

  get intermediaryCountry(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.country) as UntypedFormControl;
  }

  get nameError(): string {
    const error = this.mapErrors(this.nameModel.label, this.name, 140);
    return error === undefined ? '' : error;
  }

  get accountNumberError(): string {
    const error = this.mapErrors(
      this.accountNumberModel.label,
      this.accountNumber,
      34
    );
    return error === undefined ? '' : error;
  }

  get streetNumberError(): string | undefined {
    const error = this.mapErrors(this.streetNumberModel.label, this.streetNumber, 16);
    return error === undefined ? '' : error;
  }

  get streetNameError(): string | undefined {
    const error = this.mapErrors(this.streetNameModel.label, this.streetName, 70);
    return error === undefined ? '' : error;
  }

  get apartmentSuiteEtcError(): string | undefined {
    const error = this.mapErrors(this.apartmentSuiteModel.label, this.apartmentSuiteEtc, 70);
    return error === undefined ? '' : error;
  }

  get cityError(): string | undefined {
    const error = this.mapErrors(this.cityModel.label, this.city, 35);
    return error === undefined ? '' : error;
  }
  
  get departmentError(): string | undefined {
    const error = this.mapErrors(this.departmentModel.label, this.department, 70);
    return error === undefined ? '' : error;
  }

  get stateError(): string | undefined {
    const error = this.mapErrors(this.stateModel.label, this.state, 35);
    return error === undefined ? '' : error;
  }
  get buildingNameError(): string | undefined {
    const error = this.mapErrors(this.buildingNameModel.label, this.buildingName, 35);
    return error === undefined ? '' : error;
  }

  get zipError(): string | undefined {
    const errorMap = isoInputZipErrorMap('ZIP code', 16);
    const errorKeys = Object.keys(this.zipCode?.errors ?? {});

    if (errorKeys.length > 0) {
      return errorMap[
        errorKeys[0] as keyof typeof isoInputZipErrorMap
      ];
    }

    return '';
  }

  get intlZipError(): string | undefined {
    const error = this.mapErrors(this.intlZipModel.label, this.zipCode, 16);
    return error === undefined ? '' : error;
  }
  get floorError(): string | undefined {
    const error = this.mapErrors(this.floorModel.label, this.floor, 70);
    return error === undefined ? '' : error;
  }
  
  get phoneError(): string {
    const error = this.mapErrors(this.phoneModel.label, this.phone, 40);
    return error === undefined ? '' : error;
  }

  get townLocationNameError(): string | undefined {
    const error = this.mapErrors(this.townLocationNameModel.label, this.townLocationName, 35);
    return error === undefined ? '' : error;
  }

  get districtError(): string | undefined {
    const error = this.mapErrors(this.districtModel.label, this.district, 35);
    return error === undefined ? '' : error;
  }

  get isInternational(): boolean {
    return this.parentForm?.controls?.intermediaryCountry?.value?.trim() !== 'US';
  }

  needToResetPrepopulatedIntermediaryFields(wireInfo: WireInfo): boolean {
    return (
      (this.intermediarySelection?.disabled
        ? this.intermediarySelection?.disabled
        : false) &&
      (wireInfo.detailsCallSucceeded || wireInfo.detailsCallFailed)
    );
  }

  onPhoneInput(): void {
    if (!this.phone) {
      return;
    }

    const phone = this.phone;
    if (phone?.value?.length < 1) {
      return;
    }
    let temp = phone.value.replace(/[^\d]/g, '');
    const group = temp.match(/^(.{3})(.{1,3})(.{0,4})(.{0,4})(.*)/);

    if (group) {
      let ext = '';
      let fourDigitGroup = '';
      if (group[4]) {
        ext = ` Ext. ${group[4]}`;
      }

      if (group[3]) {
        fourDigitGroup = `-${group[3]}`;
      }
      temp = `${group[1]}-${group[2]}${fourDigitGroup}${ext}`;
    }

    const tenDigits = 10;
    const twoHyphens = 2;
    const extAbbrevPlusSpace = 6;
    const extDigits = 4;

    const maxLength = tenDigits + twoHyphens + extAbbrevPlusSpace + extDigits;
    if (temp.length > maxLength) {
      temp = temp.substring(0, maxLength);
    }

    this.phone.setValue(temp);
  }

  formatIntermediaryPhoneNumber(wireInfo: WireInfo): string {
    let phoneNumber = [
      wireInfo.receivingBankDetails.interPhoneArea,
      wireInfo.receivingBankDetails.interPhoneNum1,
      wireInfo.receivingBankDetails.interPhoneNum2,
    ]
      .filter(Boolean)
      .join('-');

    let phoneNumberWithExt = [
      phoneNumber,
      wireInfo.receivingBankDetails.interPhoneExt,
    ]
      .filter(Boolean)
      .join(' Ext. ');

    return phoneNumberWithExt;
  }

  showIntermediaryWarningBanner() {
    if(this.accountNumber && this.parentForm.controls.aba?.value && 
      this.parentForm.controls.aba?.value != '') {
      return this.parentForm.controls.aba.value == this.accountNumber.value;
    } else {
      return false;
    }
  }

  private mapErrors(
    name: string,
    control: UntypedFormControl | undefined,
    maxLength: number
  ): string | undefined {
    const errorMap = isoInputFieldErrorMap(name, maxLength);
    const errorKeys = Object.keys(control?.errors ?? {});

    if (errorKeys.length > 0) {
      return errorMap[errorKeys[0] as keyof typeof isoInputFieldErrorMap];
    }

    return '';
  }
}
